import { ColumnProps } from "antd/lib/table";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ClientContext from "../../../context/clients/client/context";
import { ISort, IStaticFilter } from "../../../models/base/base-query";
import {
  getBooleanStatus,
  getUserStatusColor,
} from "../../../models/city/enum";
import { IClient } from "../../../models/client/response";
import eventManager, { EVENT_SUCCESS } from "../../../utils/events";
import { tableOnChange } from "../../../utils/helpers/table-sorts-filters";
import Space from "../../general/antd/space";
import Tag from "../../general/antd/tag";
import DeleteBtn from "../../general/table-components/actions/delete-btn";
import EditBtn from "../../general/table-components/actions/edit-btn";
import ViewBtn from "../../general/table-components/actions/view-btn";
import Table from "../../general/table-components/table";
import { ImCheckmark, ImCross } from "react-icons/im";
import ChatBtn from "../../general/table-components/actions/chat-btn";
import Button from "../../general/antd/button";
import { MoreOutlined } from "@ant-design/icons";
import Popconfirm from "../../general/antd/pop-confirm";
import { Dropdown } from "antd";
import AppContext from "../../../context/app/context";

interface IProps { }

const ClientTable: React.FC<IProps> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { list, actions, loading, query } = useContext(ClientContext);
  const { screenSize } = useContext(AppContext);

  const columns: ColumnProps<IClient>[] = [
    {
      title: t("id"),
      dataIndex: "id",
      key: "id",
      sorter: {
        multiple: 1,
      },

      align: "center",
    },
    {
      title: t("first_name"),
      dataIndex: "firstName",
      minWidth:100,
      sorter: {
        multiple: 2,
      },
      align: "center",
      key: "firstName",
      render: (_, record) => {
        return <>{record?.user?.firstName ?? "-"}</>;
      },
    },
    {
      title: t("last_name"),
      dataIndex: "lastName",
      align: "center",
      minWidth:110,
      sorter: {
        multiple: 3,
      },
      key: "lastName",
      render: (_, record) => {
        return <>{record?.user?.lastName ?? "-"}</>;
      },
    },
    {
      title: t("city"),
      dataIndex: "city",
      align: "center",
      key: "city",
      render: (_, record) => {
        return <>{record?.city?.name ?? "-"}</>;
      },
    },
    {
      title: t("accepted_orders_count"),
      dataIndex: "acceptedOrdersCount",
      align: "center",
      minWidth:150,
      key: "acceptedOrdersCount",
      render: (_, record) => {
        return <>{record?.acceptedOrdersCount ?? "-"}</>;
      },
    },
    {
      title: t("hasOneOrder"),
      dataIndex: "hasOneOrder",
      align: "center",
      key: "hasOneOrder",
      minWidth:110,
      render: (_, record) => {
        return (
          <>
            {record.hasOneOrder ? (
              <ImCheckmark color="green" />
            ) : (
              <ImCross color="red" />
            )}
          </>
        );
      },
    },
    {
      title: t("active_points"),
      dataIndex: "activePoints",
      align: "center",
      minWidth:140,
      key: "activePoints",
      sorter: {
        multiple: 4,
      },
      render: (_, record) => {
        return <>{record?.activePoints ?? "-"}</>;
      },
    },
    {
      title: t("expired_points"),
      dataIndex: "expiredPoints",
      align: "center",
      minWidth:155,
      key: "expiredPoints",
      sorter: {
        multiple: 4,
      },
      render: (_, record) => {
        return <>{record?.expiredPoints ?? "-"}</>;
      },
    },
    {
      title: t("account_status"),
      dataIndex: "status",
      align: "center",
      minWidth:90,
      key: "status",
      render: (_, record) => {
        return (
          <Tag
            title={getBooleanStatus(record?.user?.status)}
            color={getUserStatusColor(record?.user?.status)}
          />
        );
      },
    },

    {
      title: t("phone"),
      dataIndex: "phone",
      key: "phone",
      align: "center",
      render: (_, record) => {
        return <>{record?.user?.cleanMobile}</>;
      },
    },
    {
      title: t("subscription_date"),
      dataIndex: "createdAt",
      key: "createdAt",
      align: "center",
      minWidth:120,
      sorter: {
        multiple: 5,
      },
      render: (createdAt) => {
        return moment(createdAt).format("YYYY-MM-DD");
      },
    },
    {
      title: t("actions"),
      width: screenSize != 'mobileOrTablet' ? 250 : 90,
      fixed: "right",
      dataIndex: "",
      align: "center",
      key: "x",
      render: (_: any, record) => (
        screenSize === 'mobileOrTablet' ? (
          <Dropdown
            trigger={["click"]}
            menu={{
              items: [
                {
                  key: 'view',
                  label: (
                    <Button
                      type="link"
                      onClick={(e) => {
                        const url = `/clients/details/${record?.id}`;
                        if (e.ctrlKey || e.metaKey) {
                          window.open(url, "_blank");
                        } else {
                          navigate(url);
                        }
                      }}
                    >
                      {t("view")}
                    </Button>
                  ),
                },
                {
                  key: 'edit',
                  label: (
                    <Button
                      type="link"
                      onClick={(e) => {
                        const url = `/clients/update/${record?.id}`;
                        if (e.ctrlKey || e.metaKey) {
                          window.open(url, "_blank");
                        } else {
                          navigate(url);
                        }
                      }}
                    >
                      {t("edit")}
                    </Button>
                  ),
                },
                {
                  key: 'delete',
                  label: (
                    <Popconfirm
                      title={t("confirm")}
                      onConfirm={async () => {
                        await actions.deleteClient(record?.id);
                        eventManager.emit(EVENT_SUCCESS);
                      }}
                    >
                      <Button type="link" danger>
                        {t("delete")}
                      </Button>
                    </Popconfirm>
                  ),
                },
                {
                  key: 'chat',
                  label: (
                    <ChatBtn userId={record.id} type="link" />
                  ),
                },
              ],
            }}
          >
            <Button icon={<MoreOutlined />} />
          </Dropdown>
        ) : (
          <Space>
            <ViewBtn
              onClick={(e) => {
                const url = `/clients/details/${record?.id}`;
                if (e.ctrlKey || e.metaKey) {
                  window.open(url, "_blank");
                } else {
                  navigate(url);
                }
              }}
            />
            <EditBtn
              onClick={(e) => {
                const url = `/clients/update/${record?.id}`;
                if (e.ctrlKey || e.metaKey) {
                  window.open(url, "_blank");
                } else {
                  navigate(url);
                }
              }}
            />
            <DeleteBtn
              onConfirm={async () => {
                await actions.deleteClient(record?.id);
                eventManager.emit(EVENT_SUCCESS);
              }}
              loading={loading.includes("delete")}
            />
            <ChatBtn userId={record.id} />
          </Space>
        )
      ),
    }
  ];

  let tableFiltersProps = {};
  const [sorts, setSorts] = useState<ISort[]>([]);
  const [, setStaticFilters] = useState<IStaticFilter[]>(query?.filters ?? []);

  useEffect(() => {
    sorts.length > 0 &&
      actions.setQuery({
        ...query,
        orders: sorts,
      });
  }, [sorts]);

  return (
    <>
      <Table<any>
        rowKey="id"
        showPagination={true}
        columns={columns}
        dataSource={list?.data ?? []}
        loading={loading.includes("list") || list == undefined}
        size="small"
        total={list?.total}
        pageSize={query.perPage}
        page={query.page}
        // scroll={{
        //   x: 1300,
        // }}
        onPaginationChange={(page, pageSize) => {
          actions.setQuery({
            ...query,
            page,
            perPage: pageSize,
          });
        }}
        onChange={(_, tableFilters, tabelSorters) => {
          tableOnChange(
            tableFilters,
            tabelSorters,
            tableFiltersProps,
            setSorts,
            setStaticFilters
          );
        }}
      />
    </>
  );
};

export default ClientTable;
